import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { applyThemeParams, readTelegramContext, type TelegramContext } from '@/lib/telegram';
import { LocaleContext } from '@/lib/i18n';
import { exchangeInitDataForSession } from '@/lib/auth';
import { HomePage } from '@/pages/HomePage';
import { GrouponListPage } from '@/pages/GrouponListPage';
import { GrouponDetailPage } from '@/pages/GrouponDetailPage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { OrderStatusPage } from '@/pages/OrderStatusPage';
import { MyOrdersPage } from '@/pages/MyOrdersPage';
import { MePage } from '@/pages/MePage';
import { SearchPage } from '@/pages/SearchPage';
import { MerchantDetailPage } from '@/pages/MerchantDetailPage';
import { DiscoverPage } from '@/pages/DiscoverPage';
import { DiscoverPostDetailPage } from '@/pages/DiscoverPostDetailPage';
import { FavoritesPage } from '@/pages/FavoritesPage';
import { MyCustomerServicePage } from '@/pages/MyCustomerServicePage';
import { ConsumerCsConversationPage } from '@/pages/ConsumerCsConversationPage';
import { MessagesPlaceholderPage } from '@/pages/MessagesPlaceholderPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { useAuth } from '@/lib/use-auth';
import type { Locale } from '@sabay/i18n';

const SUPPORTED_LOCALES: readonly Locale[] = ['zh-CN', 'km-KH', 'en'];

function isSupportedLocale(value: string | null | undefined): value is Locale {
  return value !== null && value !== undefined && SUPPORTED_LOCALES.includes(value as Locale);
}

export function App(): React.ReactElement {
  const [ctx, setCtx] = useState<TelegramContext | null>(null);

  useEffect(() => {
    const resolved = readTelegramContext();
    applyThemeParams(resolved.themeParams);
    setCtx(resolved);

    // Fire-and-forget initData → session token 交换（ADR-0004 §4.2）。
    // 结果写入 memory-only auth store；UI 通过 `useAuth()` 消费，状态错误不阻塞渲染。
    void exchangeInitDataForSession(resolved.initDataRaw).catch(() => {
      // auth-store 已记 error 态；此处吞异常避免 unhandled rejection
    });
  }, []);

  if (!ctx) {
    return (
      <main className="min-h-screen flex items-center justify-center p-6 text-neutral-500">
        Loading…
      </main>
    );
  }

  return <AppRoutes ctx={ctx} />;
}

function AppRoutes({ ctx }: { ctx: TelegramContext }): React.ReactElement {
  const auth = useAuth();
  // P0-D-02：用户在 /me 页面手动设置 preferredLocale 后立刻生效；
  // 否则按 Telegram WebApp languageCode（ctx.locale）。
  const locale = useMemo<Locale>(() => {
    const preferred = auth.user?.preferredLocale;
    if (isSupportedLocale(preferred)) return preferred;
    return ctx.locale;
  }, [auth.user?.preferredLocale, ctx.locale]);

  return (
    <LocaleContext.Provider value={locale}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage ctx={ctx} />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/discover/:id" element={<DiscoverPostDetailPage />} />
          <Route path="/merchants/:id" element={<MerchantDetailPage />} />
          <Route path="/groupons" element={<GrouponListPage />} />
          <Route path="/groupons/:id" element={<GrouponDetailPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/orders/:id" element={<OrderStatusPage />} />
          <Route path="/me" element={<MePage />} />
          <Route path="/me/orders" element={<MyOrdersPage />} />
          <Route path="/me/favorites" element={<FavoritesPage />} />
          <Route path="/me/cs" element={<MyCustomerServicePage />} />
          <Route path="/me/cs/conversations/:conversationId" element={<ConsumerCsConversationPage />} />
          <Route path="/messages" element={<MessagesPlaceholderPage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </LocaleContext.Provider>
  );
}
